// Parsers de importación de contraseñas. Módulo puro (sin React ni DOM) para
// poder probarlo con Node; lo usa components/ImportExport.js.
//
// Todos devuelven entradas normalizadas:
//   { title, type, username, password, url, notes, tags[], favorite,
//     fields?: {...campos de FIELD_DEFS (itemTypes.js)}, raw?: {...data completo SecureGate},
//     otp?: 'otpauth://… o secreto' }
// `type` es siempre un tipo canónico de itemTypes.js (login, card, identity,
// note, password, ssh, api, database, oauth, subscription, passkey).

import { otpFromImportString, splitOtpDuplicates, normalizeOtpEntry } from './otpParsers.js';

// ---------------------------------------------------------------- CSV

function detectDelimiter(text) {
  let inQuotes = false;
  const count = { ',': 0, ';': 0, '\t': 0 };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') inQuotes = !inQuotes;
    else if (!inQuotes && c === '\n') break;
    else if (!inQuotes && c in count) count[c]++;
  }
  return Object.keys(count).sort((a, b) => count[b] - count[a])[0] || ',';
}

/** CSV RFC 4180: comillas, "" escapadas, saltos de línea dentro de un campo, CRLF. */
export function parseCsv(input) {
  const text = String(input || '').replace(/^﻿/, '');
  const delim = detectDelimiter(text);
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += c;
    } else if (c === '"' && field === '') inQuotes = true;
    else if (c === delim) { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((v) => v.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((v) => v.trim() !== '')) rows.push(row);
  return rows;
}

export function csvRecords(text) {
  const rows = parseCsv(text);
  if (rows.length < 2) return { headers: [], records: [] };
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const records = rows.slice(1).map((r) => {
    const rec = {};
    headers.forEach((h, i) => { if (h && !(h in rec)) rec[h] = (r[i] ?? '').trim(); });
    return rec;
  });
  return { headers, records };
}

const pick = (rec, ...keys) => {
  for (const k of keys) if (rec[k]) return rec[k];
  return '';
};
const truthy = (v) => ['1', 'true', 'yes', 'y', 'si', 'sí'].includes(String(v || '').trim().toLowerCase());

// ---------------------------------------------------------------- helpers

const TYPE_ALIASES = {
  login: 'login', note: 'note', securenote: 'note', 'secure note': 'note', card: 'card', creditcard: 'card',
  'credit card': 'card', identity: 'identity', password: 'password', ssh: 'ssh', sshkey: 'ssh', 'ssh key': 'ssh',
  api: 'api', apikey: 'api', apicredential: 'api', database: 'database', oauth: 'oauth',
  subscription: 'subscription', passkey: 'passkey', softwarelicense: 'note',
};
export const normalizeType = (raw, fallback = 'login') => TYPE_ALIASES[String(raw || '').trim().toLowerCase()] || fallback;

export function domainOf(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  try {
    return new URL(s.includes('://') ? s : `https://${s}`).hostname.replace(/^www\./, '');
  } catch {
    return s;
  }
}

const KEY_RE = /BEGIN [A-Z ]*PRIVATE KEY|^ssh-(rsa|ed25519)|^ecdsa-sha2-/;

function finish(e) {
  const entry = {
    title: (e.title || '').trim(),
    type: e.type || 'login',
    username: e.username || '',
    password: e.password || '',
    url: e.url || '',
    notes: e.notes || '',
    tags: [...new Set((e.tags || []).map((t) => String(t).trim()).filter(Boolean))],
    favorite: !!e.favorite,
  };
  if (e.fields) entry.fields = e.fields;
  if (e.raw) entry.raw = e.raw;
  if (e.otp) entry.otp = e.otp;
  if (!entry.title) entry.title = domainOf(entry.url) || entry.username || 'Imported entry';

  // Sin tipo explícito (CSV de navegador): se deduce solo lo inequívoco.
  if (!e.type) {
    if (KEY_RE.test(entry.password)) {
      entry.type = 'ssh';
      entry.fields = { ...(entry.fields || {}), sshKey: entry.password };
      entry.password = '';
    } else if (!entry.username && !entry.password && !entry.url && entry.notes) entry.type = 'note';
    else if (!entry.username && entry.password && !entry.url) entry.type = 'password';
    else entry.type = 'login';
  }
  return entry;
}

const appendNotes = (notes, extra) => (extra ? (notes ? `${notes}\n\n${extra}` : extra) : notes);

// ---------------------------------------------------------------- CSV de gestores y navegadores

/**
 * Chrome/Edge/Brave, Firefox, Safari, Bitwarden, 1Password, LastPass, el CSV
 * propio de SecureGate y CSV genérico: se guía por la cabecera, no por el origen.
 */
export function parseCsvEntries(text) {
  const { headers, records } = csvRecords(text);
  const isLastPass = headers.includes('grouping');
  const out = [];
  for (const rec of records) {
    if (truthy(rec.archived) || truthy(rec.deleted)) continue;
    const title = pick(rec, 'name', 'title', 'account name');
    if (title === '__otp_entries__') continue;

    let tags = [];
    if (isLastPass) tags = rec.grouping.split('\\');
    else if (rec.folder) tags = [rec.folder];
    else if (rec.tags) tags = rec.tags.split(/[;,]/);
    else if (rec.group) tags = [rec.group];

    let notes = pick(rec, 'notes', 'note', 'extra', 'comments', 'comment');
    if (rec.httprealm) notes = appendNotes(notes, `HTTP realm: ${rec.httprealm}`);
    if (rec.fields) notes = appendNotes(notes, rec.fields); // Bitwarden: campos personalizados

    const entry = {
      title,
      username: pick(rec, 'username', 'login_username', 'login', 'email', 'user', 'user name', 'login name'),
      password: pick(rec, 'password', 'login_password', 'pass'),
      url: pick(rec, 'url', 'login_uri', 'website', 'web site', 'site', 'location', 'hostname'),
      notes,
      tags,
      favorite: truthy(pick(rec, 'favorite', 'fav', 'favourite', 'starred')),
      type: rec.type ? normalizeType(rec.type, '') || undefined : undefined,
      otp: pick(rec, 'login_totp', 'totp', 'otpauth', 'otp', '2fa'),
    };
    if (!(entry.title || entry.username || entry.password || entry.url || entry.notes)) continue;
    out.push(finish(entry));
  }
  return out;
}

// ---------------------------------------------------------------- Bitwarden JSON

const yy = (y) => String(y || '').slice(-2);

export function parseBitwardenJson(data) {
  if (data?.encrypted === true || data?.encKeyValidation_DO_NOT_EDIT) return { entries: [], error: 'encrypted' };
  const folders = new Map((data.folders || []).map((f) => [f.id, f.name]));
  const entries = (data.items || []).map((it) => {
    const e = {
      title: it.name,
      notes: it.notes || '',
      tags: it.folderId && folders.get(it.folderId) ? [folders.get(it.folderId)] : [],
      favorite: !!it.favorite,
      type: { 1: 'login', 2: 'note', 3: 'card', 4: 'identity' }[it.type] || 'login',
    };
    if (it.login) {
      e.username = it.login.username || '';
      e.password = it.login.password || '';
      const uris = (it.login.uris || []).map((u) => u.uri).filter(Boolean);
      e.url = uris[0] || '';
      if (uris.length > 1) e.notes = appendNotes(e.notes, uris.slice(1).map((u) => `URL: ${u}`).join('\n'));
      e.otp = it.login.totp || '';
    }
    if (it.card) {
      e.fields = {
        cardHolder: it.card.cardholderName || '', cardNumber: it.card.number || '', cardType: it.card.brand || '',
        expiryDate: it.card.expMonth && it.card.expYear ? `${String(it.card.expMonth).padStart(2, '0')}/${yy(it.card.expYear)}` : '',
        cvv: it.card.code || '',
      };
    }
    if (it.identity) {
      const i = it.identity;
      e.fields = {
        firstName: i.firstName || '', lastName: i.lastName || '', email: i.email || '', phone: i.phone || '',
        company: i.company || '', passportNumber: i.passportNumber || '', socialSecurity: i.ssn || '',
        licenseNumber: i.licenseNumber || '',
        address: [i.address1, i.address2, i.address3, i.city, i.state, i.postalCode, i.country].filter(Boolean).join(', '),
      };
    }
    const custom = (it.fields || []).filter((f) => f.name && f.value).map((f) => `${f.name}: ${f.value}`).join('\n');
    e.notes = appendNotes(e.notes, custom);
    return finish(e);
  });
  return { entries, error: null };
}

// ---------------------------------------------------------------- 1Password (1PUX / JSON)

const OP_CATEGORY = { '001': 'login', '002': 'card', '003': 'note', '004': 'identity', '005': 'password', '102': 'database', '112': 'api', '114': 'ssh' };

function opValue(v) {
  if (v == null) return '';
  if (typeof v !== 'object') return String(v);
  if (v.string != null) return String(v.string);
  if (v.concealed != null) return String(v.concealed);
  if (v.email) return typeof v.email === 'object' ? v.email.email_address || '' : String(v.email);
  if (v.url != null) return String(v.url);
  if (v.phone != null) return String(v.phone);
  if (v.creditCardNumber != null) return String(v.creditCardNumber);
  if (v.creditCardType != null) return String(v.creditCardType);
  if (v.totp != null) return String(v.totp);
  if (v.monthYear != null) { const s = String(v.monthYear); return s.length === 6 ? `${s.slice(4)}/${s.slice(2, 4)}` : s; }
  if (v.date != null) return new Date(v.date * 1000).toISOString().slice(0, 10);
  if (v.address) return Object.values(v.address).filter(Boolean).join(', ');
  if (v.reference != null || v.menu != null || v.gender != null) return String(v.menu ?? v.gender ?? '');
  return '';
}

export function parse1PuxExport(data) {
  const entries = [];
  for (const account of data.accounts || []) {
    for (const vault of account.vaults || []) {
      for (const item of vault.items || []) {
        if (item.state === 'archived' || item.trashed) continue;
        const ov = item.overview || {};
        const det = item.details || {};
        const type = OP_CATEGORY[item.categoryUuid] || 'note';
        const e = {
          title: ov.title, type, url: ov.url || ov.urls?.[0]?.url || '', notes: det.notesPlain || '',
          tags: ov.tags || [], favorite: (item.favIndex || 0) > 0, fields: {},
        };
        for (const f of det.loginFields || []) {
          if (f.designation === 'username') e.username = f.value || '';
          if (f.designation === 'password') e.password = f.value || '';
        }
        if (det.password) e.password = e.password || det.password;

        // Campos de secciones: se consumen los conocidos y el resto va a notas.
        const extra = [];
        for (const sec of det.sections || []) {
          for (const f of sec.fields || []) {
            const value = opValue(f.value);
            if (!value) continue;
            const id = String(f.id || f.title || '').toLowerCase();
            if (f.value?.totp != null) { e.otp = e.otp || value; continue; }
            if (type === 'card') {
              const map = { ccnum: 'cardNumber', cardholder: 'cardHolder', expiry: 'expiryDate', cvv: 'cvv', type: 'cardType' };
              if (map[id]) { e.fields[map[id]] = value; continue; }
            } else if (type === 'identity') {
              const map = { firstname: 'firstName', lastname: 'lastName', email: 'email', defphone: 'phone', company: 'company', address: 'address' };
              if (map[id]) { e.fields[map[id]] = value; continue; }
            } else if (type === 'database') {
              const map = { hostname: 'dbHost', port: 'dbPort', database: 'dbName', username: 'username', password: 'password' };
              if (map[id]) { if (map[id] === 'username' || map[id] === 'password') e[map[id]] = value; else e.fields[map[id]] = value; continue; }
            } else if (type === 'api' && id === 'credential') { e.fields.apiKey = value; continue; }
            else if (type === 'ssh' && (id === 'private_key' || id === 'privatekey')) { e.fields.sshKey = value; continue; }
            extra.push(`${f.title || f.id}: ${value}`);
          }
        }
        e.notes = appendNotes(e.notes, extra.join('\n'));
        entries.push(finish(e));
      }
    }
  }
  return entries;
}

// ---------------------------------------------------------------- Formato propio de SecureGate

const SG_DROP = new Set(['name', 'tags', 'createdAt', 'updatedAt', '_id', 'id']);

/**
 * Export JSON de SecureGate. Dos formas (server/routes/data.js, processBulkExport):
 *  - plano:   [{ name, type, url, username, password, notes, tags, ... }] (también passkeys)
 *  - 'encrypted' (version 2): { version, items: [{ name, data, tags }] }, con el `data` completo.
 * El registro de 2FA (`__otp_entries__`) no es un ítem: sus entradas salen aparte.
 */
export function parseSecureGateJson(data) {
  const list = Array.isArray(data) ? data : data.items || [];
  const entries = [];
  const otpEntries = [];
  for (const it of list) {
    if (!it || typeof it !== 'object') continue;
    const d = it.data && typeof it.data === 'object' ? it.data : null;
    const kind = d?.type || it.type;
    if (it.name === '__otp_entries__' || kind === 'otp_vault') {
      for (const o of d?.entries || []) {
        const e = normalizeOtpEntry(o || {});
        if (e) otpEntries.push(e);
      }
      continue;
    }
    const type = normalizeType(kind);
    if (d) {
      const raw = { ...d };
      delete raw.vault;
      entries.push(finish({
        title: it.name, type, username: d.username || '', password: d.password || '', url: d.url || '',
        notes: d.notes || '', tags: it.tags || d.tags || [], favorite: !!d.favorite, raw,
      }));
    } else if (type === 'passkey') {
      const raw = {};
      for (const [k, v] of Object.entries(it)) if (!SG_DROP.has(k)) raw[k] = v;
      entries.push(finish({ title: it.name, type, tags: it.tags, raw }));
    } else {
      entries.push(finish({
        title: it.name, type, username: it.username, password: it.password, url: it.url,
        notes: it.notes, tags: it.tags,
      }));
    }
  }
  return { entries, otpEntries };
}

// ---------------------------------------------------------------- Detección y punto de entrada

const looksSecureGateFlat = (arr) => arr.length > 0 && arr.every((x) => x && typeof x === 'object' && typeof x.name === 'string' && typeof x.type === 'string');

function genericJsonArray(arr) {
  return arr.map((it) => finish({
    title: it.name || it.title, username: it.username || it.login?.username || '',
    password: it.password || it.login?.password || '', url: it.url || it.login?.uris?.[0]?.uri || '',
    notes: it.notes || '', tags: it.tags || [], favorite: !!it.favorite,
  }));
}

/**
 * Texto de un fichero → { entries, otpEntries, error, format }.
 * error: 'encrypted' | 'unknown' | null. otpEntries: códigos 2FA ya deduplicados.
 */
export function parseImportText(text) {
  const t = String(text || '').replace(/^﻿/, '').trim();
  let entries = [];
  let otpEntries = [];
  let format = 'csv';

  if (t.startsWith('{') || t.startsWith('[')) {
    let json;
    try { json = JSON.parse(t); } catch { return { entries: [], otpEntries: [], error: 'unknown', format: 'json' }; }
    if (!Array.isArray(json) && json.accounts) {
      format = '1pux';
      entries = parse1PuxExport(json);
    } else if (!Array.isArray(json) && Array.isArray(json.items) && (json.rfidId !== undefined || json.items[0]?.data?.type !== undefined)) {
      format = 'securegate';
      ({ entries, otpEntries } = parseSecureGateJson(json));
    } else if (!Array.isArray(json) && (Array.isArray(json.items) || json.encrypted !== undefined)) {
      format = 'bitwarden';
      const r = parseBitwardenJson(json);
      if (r.error) return { entries: [], otpEntries: [], error: r.error, format };
      entries = r.entries;
    } else if (Array.isArray(json) && looksSecureGateFlat(json)) {
      format = 'securegate';
      ({ entries, otpEntries } = parseSecureGateJson(json));
    } else if (Array.isArray(json)) {
      format = 'json';
      entries = genericJsonArray(json);
    } else return { entries: [], otpEntries: [], error: 'unknown', format: 'json' };
  } else {
    entries = parseCsvEntries(t);
  }

  // Códigos 2FA que traen los propios ítems (columna totp / campo OTP de 1PUX).
  const fromItems = [];
  for (const e of entries) {
    if (!e.otp) continue;
    const o = otpFromImportString(e.otp, { issuer: e.title, account: e.username });
    if (o) fromItems.push(o);
    else delete e.otp;
  }
  otpEntries = splitOtpDuplicates([...otpEntries, ...fromItems]).fresh;
  return { entries, otpEntries, error: null, format };
}

// ---------------------------------------------------------------- Construcción, duplicados y lotes

const clip = (s, n) => String(s || '').slice(0, n);

/** Entrada → objeto que espera POST /api/data/bulk-import: { name, description, data, tags }. */
export function toBulkItem(entry, vaultId) {
  const data = { ...(entry.raw || {}) };
  data.type = entry.type;
  data.vault = vaultId || 'personal';
  data.notes = entry.notes || '';
  if (entry.username) data.username = entry.username;
  if (entry.password) data.password = entry.password;
  if (entry.url) data.url = entry.url;
  if (entry.favorite) data.favorite = true;
  for (const [k, v] of Object.entries(entry.fields || {})) if (v) data[k] = v;
  const name = clip(entry.title, 250);
  const tags = [...new Set([...entry.tags.map((x) => clip(x, 50)), 'imported'])];
  // La descripción va en claro en la base de datos: solo el título, nunca las notas.
  return { name, description: name, data, tags };
}

const normUrl = (u) => String(u || '').trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');

/** Clave de duplicado: nombre + usuario + URL, sin mayúsculas ni esquema. */
export const dupKey = (name, username, url) => [String(name || '').trim().toLowerCase(), String(username || '').trim().toLowerCase(), normUrl(url)].join('\u0001');

export const entryDupKey = (entry) => dupKey(entry.title, entry.username, entry.url);

/** Items ya presentes en la bóveda destino → Set de claves. `existing`: [{ name, username, url }]. */
export const buildDupIndex = (existing) => new Set(existing.map((x) => dupKey(x.name, x.username, x.url)));

export function markDuplicates(entries, dupIndex) {
  return entries.map((e) => ({ entry: e, duplicate: dupIndex.has(entryDupKey(e)) }));
}

/** Trocea en lotes por nº de items y por tamaño estimado, siempre ≥ 1 item por lote. */
export function chunkItems(items, { maxItems = 200, maxBytes = 4 * 1024 * 1024 } = {}) {
  const chunks = [];
  let cur = [];
  let bytes = 0;
  for (const it of items) {
    const size = JSON.stringify(it).length;
    if (cur.length && (cur.length >= maxItems || bytes + size > maxBytes)) {
      chunks.push(cur);
      cur = [];
      bytes = 0;
    }
    cur.push(it);
    bytes += size;
  }
  if (cur.length) chunks.push(cur);
  return chunks;
}
